/**
 * Sharing files in a conversation (chat, team channel, meeting chat).
 *
 * Every file is uploaded into the conversation's room (file-service checks the
 * sender is a member, and only members and the meeting's guests can open it),
 * then posted as a `file` message whose metadata points at it. Images are
 * `file` messages too: messaging-service only accepts text, file and call_invite.
 */
import { api, API_BASE } from './api';

export interface AttachmentMeta {
  fileId: string;
  filename: string;
  mimeType?: string;
  size?: number;
}

/** The attachment a message carries, or null for ordinary messages. */
export function attachmentOf(type: string | undefined, metadata: Record<string, unknown> | undefined): AttachmentMeta | null {
  if (!metadata || typeof metadata['fileId'] !== 'string' || !metadata['fileId']) return null;
  if (type && type !== 'file' && type !== 'image') return null;
  return {
    fileId: metadata['fileId'],
    filename: typeof metadata['filename'] === 'string' ? metadata['filename'] : 'file',
    mimeType: typeof metadata['mimeType'] === 'string' ? metadata['mimeType'] : undefined,
    size: typeof metadata['size'] === 'number' ? metadata['size'] : undefined,
  };
}

export function isImageAttachment(file: AttachmentMeta): boolean {
  return file.mimeType?.startsWith('image/') === true || /\.(png|jpe?g|gif|webp|avif|bmp)$/i.test(file.filename);
}

/** Uploads one file into the room; returns its attachment metadata. */
export async function uploadToRoom(roomId: string, file: File): Promise<AttachmentMeta> {
  const form = new FormData();
  form.append('file', file);
  const token = (await api.freshToken()) ?? '';
  const res = await fetch(`${API_BASE}/v1/files?roomId=${encodeURIComponent(roomId)}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const uploaded = res.ok
    ? ((await res.json()) as { data?: { id?: string; name?: string; mime_type?: string; size_bytes?: number } }).data
    : undefined;
  if (!uploaded?.id) throw new Error('upload failed');
  return {
    fileId: uploaded.id,
    filename: uploaded.name ?? file.name,
    mimeType: uploaded.mime_type || file.type || undefined,
    size: Number(uploaded.size_bytes ?? file.size),
  };
}

export interface PostedFileMessage {
  id: string;
  sender_id: string;
  content: string;
  created_at: string;
  metadata?: Record<string, unknown>;
}

/**
 * Uploads and posts each file in turn. Returns the posted messages; files that
 * failed are passed to onFailed (by name) and skipped.
 */
export async function shareFilesToRoom(
  roomId: string,
  files: File[],
  sender: { id: string; displayName: string },
  onFailed: (fileName: string) => void,
): Promise<PostedFileMessage[]> {
  const posted: PostedFileMessage[] = [];
  for (const file of files) {
    try {
      const meta = await uploadToRoom(roomId, file);
      const res = await api.post<PostedFileMessage & { message?: PostedFileMessage }>(`/v1/rooms/${roomId}/messages`, {
        content: meta.filename,
        senderId: sender.id,
        senderName: sender.displayName,
        type: 'file',
        metadata: { ...meta },
      });
      const m = res.data?.message ?? (res.data?.id ? res.data : undefined);
      if (!res.success || !m) throw new Error('post failed');
      posted.push({ ...m, metadata: m.metadata ?? { ...meta } });
    } catch {
      onFailed(file.name);
    }
  }
  return posted;
}
